import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import styled from '@emotion/styled';
import { Box, Image } from '@qga/roo-ui/components';

import CollapsiblePackageSection, {
  Heading,
} from '@shared/v2/components/CollapsiblePackageSection';
import Select from '@shared/v2/components/Select';

import Button from '@/components/Button';
import RooUiHeading from '@/components/Heading';
import useOrigins from '@/hooks/useOrigins';
import getAsset from '@/utils/getAsset';
import { openLocationPanelDataLayerEvent } from '@/v2/components/pages/PackageViewV2/utils/dataLayer';
import { useGA4Events } from '@/v2/hooks/featureFlags/ga4Events/useGA4Events';
import { handleFlyingFromDatalayerEvent } from '@/v2/utils/dataLayer/searchEvents/changeHandlers';
import { getOriginName } from '@/v2/utils/getOriginName';

interface Props {
  value?: string;
  onSubmit: Function;
  originCodes?: string[];
}

const StyledButton = styled(Button)`
  background: transparent;
  height: 48px;
  font-size: 16px;
`;

const getCtaText = (isPanelOpen: boolean) => {
  return isPanelOpen ? 'Cancel' : 'Change location';
};

const FlyingFrom = ({ value = undefined, onSubmit, originCodes }: Props) => {
  const { isReady } = useRouter();
  const shouldUseNewEvents = useGA4Events();

  const origins = useOrigins(originCodes).map((o) => ({
    ...o,
    value: o.code,
    text: o.name,
  }));

  const [isPanelOpen, setIsPanelOpen] = useState(false);

  useEffect(() => {
    if (isReady) {
      setIsPanelOpen(!value);
    }
  }, [isReady, value]);

  const togglePanel = () => {
    setIsPanelOpen((curr) => {
      if (!curr) {
        openLocationPanelDataLayerEvent();
      }
      return !curr;
    });
  };

  return (
    <CollapsiblePackageSection
      icon={
        <Image
          alt="Departing plane icon"
          src={getAsset('/images/icons/departure.svg')}
          size={[42, null, 56]}
        />
      }
      headerContent={
        <>
          {isPanelOpen ? (
            <>
              <RooUiHeading.h4>Flying from</RooUiHeading.h4>
              <Heading>Select your location</Heading>
            </>
          ) : (
            <>
              <Heading>Flying from</Heading>
              <RooUiHeading.h4>{getOriginName(value) ?? ''}</RooUiHeading.h4>
            </>
          )}
        </>
      }
      collapseButton={
        <Box>
          <StyledButton
            data-testid="FLYING_FROM_CTA"
            width={['100%', null, '240px']}
            variant="secondary"
            outline={!isPanelOpen}
            onClick={togglePanel}
          >
            {getCtaText(isPanelOpen)}
          </StyledButton>
        </Box>
      }
      sectionContent={
        <Box width={['100%', null, '240px']}>
          <Select
            data-testid="SELECT"
            id="flying-from"
            textAlign={[null, null, 'center']}
            width={['100%', null, 'min-content']}
            minWidth="202px"
            value={value || ''}
            onSelectedChange={(newValue: string) => {
              if (shouldUseNewEvents) {
                handleFlyingFromDatalayerEvent(
                  getOriginName(value),
                  getOriginName(newValue),
                );
              }
              onSubmit(newValue);
            }}
            readOnly
            options={origins}
            placeholder="Flying from"
            prefix="From"
            selectSize="lg"
          />
        </Box>
      }
      isOpen={isPanelOpen}
      showButton={!isPanelOpen || (isPanelOpen && !!value)}
    />
  );
};

export default FlyingFrom;
